import './DmaMap.scss';
import cn from 'classnames';
import { MouseEvent, ReactNode, Ref, useEffect, useMemo, useState, WheelEvent } from 'react';
import { Overlay } from 'react-bootstrap';
import { MdAdd, MdRefresh, MdRemove } from 'react-icons/md';
import { IconButton } from '@Components/IconButton';
import { Spinner } from '@Components/Spinner';
import { useSetError } from '../../redux/modals';
import { Neutral400, Neutral600 } from '../../utils/colors';
import { Appearance, Placement } from '../designConstants';
import { DMAPathMap, MAP_HEIGHT, MAP_VIEW_BOX, MAP_WIDTH } from './dma-map-constants';
import { fetchDmaPaths } from './dma-map-utils';

const BORDER_WIDTH = 1;
const BORDER_SELECTED_WIDTH = 4;

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 5;
const ZOOM_STEP = 1.2;
const WHEEL_ZOOM_IN = 1.1;
const WHEEL_ZOOM_OUT = 0.9;

const ALASKA_DMA: DMACode = '743';

export type DMACode = string;

export interface GeoData {
  impressions: number;
  responses: number;
  households: number;
  name: string;
  dma: DMACode;
  index: number;
}

export type GeoDataMap = Record<DMACode, GeoData>;
export type GeoDataObject = GeoDataMap | false | undefined;

interface TooltipData {
  x: number;
  y: number;
  dma: DMACode;
}

interface DmaMapProps {
  ref?: Ref<SVGSVGElement | null>;
  renderColor?: (dma: string) => string;
  width?: number;
  height?: number;
  renderTooltip?: (dma: DMACode) => ReactNode;
  selection?: DMACode;
  onPathLoad?: (loaded: boolean) => void;
  onClick?: (dma: string) => void;
  borderColor?: string;
  selectedBorderColor?: string;
  enableZoom?: boolean;
}

export const DmaMap = ({
  ref,
  renderColor,
  width = MAP_WIDTH,
  height = MAP_HEIGHT,
  renderTooltip = (dma: DMACode) => dma,
  selection: externalSelection,
  onPathLoad,
  onClick,
  borderColor = Neutral400,
  selectedBorderColor = Neutral600,
  enableZoom = false
}: DmaMapProps) => {
  const setError = useSetError();

  const [tooltipData, setTooltipData] = useState<TooltipData>();
  const [dmaPaths, setDmaPaths] = useState<DMAPathMap>();
  const [pathsLoading, setPathsLoading] = useState(false);
  // Without this, a persistent failure retries every render as soon as pathsLoading flips back
  // to false — an unbounded, no-backoff loop that re-hits the network and re-reports the same
  // error as fast as React can re-render, and the map never recovers.
  const [pathsLoadFailed, setPathsLoadFailed] = useState(false);

  const [zoomLevel, setZoomLevel] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (dmaPaths || pathsLoading || pathsLoadFailed) return;
    (async () => {
      setPathsLoading(true);
      try {
        const paths = await fetchDmaPaths();
        setDmaPaths(paths);
      } catch (e) {
        setPathsLoadFailed(true);
        setError({
          message: `Failed to get dma path information: ${e.message}`,
          reportError: e
        });
      }
      setPathsLoading(false);
      onPathLoad?.(true);
    })();
  }, [pathsLoading, dmaPaths, pathsLoadFailed, setError, onPathLoad]);

  const dmaMap = useMemo(() => {
    if (!dmaPaths || !renderColor) return null;
    return Object.keys(dmaPaths).map(dma => {
      const isSelected = (tooltipData?.dma || externalSelection) === dma;
      return (
        <path
          className={cn('dma-path', { 'scale-alaska': dma === ALASKA_DMA })}
          key={dma}
          d={dmaPaths[dma]?.path}
          stroke={isSelected ? selectedBorderColor : borderColor}
          strokeWidth={isSelected ? BORDER_SELECTED_WIDTH : BORDER_WIDTH}
          fill={renderColor(dma)}
          onMouseEnter={e => setTooltipData({ x: e.clientX, y: e.clientY, dma })}
          onMouseLeave={() => setTooltipData(undefined)}
          onClick={onClick ? () => onClick(dma) : undefined}
        />
      );
    });
  }, [
    dmaPaths,
    renderColor,
    tooltipData,
    externalSelection,
    borderColor,
    selectedBorderColor,
    onClick
  ]);

  // Popper virtual element anchored at the cursor. A new instance per cursor
  // position triggers Popper to recompute placement (with flip / preventOverflow
  // handling viewport edges).
  const cursorTarget = useMemo(() => {
    if (!tooltipData) return null;
    const { x, y } = tooltipData;
    return {
      getBoundingClientRect: () => DOMRect.fromRect({ x, y, width: 0, height: 0 })
    };
  }, [tooltipData]);

  // Resolve the tooltip content up front so we can skip the overlay entirely when
  // renderTooltip returns nothing (e.g. a DMA not included in the model) instead of
  // painting an empty styled box.
  const tooltipContent = tooltipData ? renderTooltip(tooltipData.dma) : undefined;

  const handleWheel = (e: WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? WHEEL_ZOOM_OUT : WHEEL_ZOOM_IN;
    setZoomLevel(level => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, level * delta)));
  };

  const handleMouseDown = (e: MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - panX, y: e.clientY - panY });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    setPanX(e.clientX - dragStart.x);
    setPanY(e.clientY - dragStart.y);
  };

  const stopDragging = () => setIsDragging(false);

  const resetZoom = () => {
    setZoomLevel(1);
    setPanX(0);
    setPanY(0);
  };

  const renderMap = () => {
    if (pathsLoading || !dmaMap) {
      return (
        <div className='dma-map-loader' style={{ width, height }}>
          <Spinner />
        </div>
      );
    }
    if (!enableZoom) {
      return (
        <svg ref={ref} viewBox={MAP_VIEW_BOX}>
          {dmaMap}
        </svg>
      );
    }
    return (
      <svg
        ref={ref}
        viewBox={MAP_VIEW_BOX}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={stopDragging}
        onMouseLeave={stopDragging}
      >
        <g transform={`translate(${panX}, ${panY}) scale(${zoomLevel})`}>{dmaMap}</g>
      </svg>
    );
  };

  return (
    <div className='dma-map' style={{ width, height }}>
      {renderMap()}

      {enableZoom && (
        <div className='dma-map-controls'>
          <IconButton
            appearance={Appearance.EMPTY}
            onClick={() => setZoomLevel(level => Math.min(MAX_ZOOM, level * ZOOM_STEP))}
          >
            <MdAdd />
          </IconButton>
          <IconButton
            appearance={Appearance.EMPTY}
            onClick={() => setZoomLevel(level => Math.max(MIN_ZOOM, level / ZOOM_STEP))}
          >
            <MdRemove />
          </IconButton>
          <IconButton appearance={Appearance.OUTLINED} onClick={resetZoom}>
            <MdRefresh />
          </IconButton>
        </div>
      )}

      {tooltipContent && cursorTarget && (
        <Overlay
          show
          target={cursorTarget}
          placement={Placement.LEFT_START}
          flip
          popperConfig={{ strategy: 'fixed' }}
        >
          {({ ref: tooltipRef, style }) => (
            <div ref={tooltipRef} style={style} className='dma-tooltip'>
              {tooltipContent}
            </div>
          )}
        </Overlay>
      )}
    </div>
  );
};
